import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCategories,
  deleteCategory,
  createDefaultCategories,
  selectCategories,
  selectCategoriesStatus
} from './categoriesSlice';
import CategoryModal from './CategoryModal';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import './Categories.css';

const Categories = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { confirm } = useConfirm();
  const categories = useSelector(selectCategories);
  const status = useSelector(selectCategoriesStatus);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  useEffect(() => {
    dispatch(fetchCategories({ tree: true }));
  }, [dispatch]);

  const handleCreateCategory = (parent = null) => {
    setEditingCategory(null);
    setParentForNew(parent);
    setIsModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setParentForNew(null);
    setIsModalOpen(true);
  };

  const handleDeleteCategory = async (category) => {
    const hasChildren = category.children && category.children.length > 0;
    const confirmMessage = hasChildren
      ? `Are you sure you want to delete "${category.name}" and all its subcategories?`
      : `Are you sure you want to delete "${category.name}"?`;

    const confirmed = await confirm(confirmMessage, {
      title: 'Delete Category',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await dispatch(deleteCategory(category.id)).unwrap();
      toast.success('Category deleted successfully');
    } catch (error) {
      toast.error(error || 'Failed to delete category');
    }
  };

  const handleCreateDefaults = async () => {
    try {
      const result = await dispatch(createDefaultCategories()).unwrap();
      toast.success(`Created ${result.created?.length || 0} categories from your products`);
      dispatch(fetchCategories({ tree: true }));
    } catch (error) {
      toast.error(error || 'Failed to create categories');
    }
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const filteredCategories = (categories || []).filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const childMatches = category.children?.some(child =>
      child.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return matchesSearch || childMatches;
  });

  const getProductCount = (category) => {
    let count = category._count?.products || 0;
    // Add children's products for parent total
    if (category.children) {
      category.children.forEach(child => {
        count += child._count?.products || 0;
      });
    }
    return count;
  };

  if (status === 'loading' && (!categories || categories.length === 0)) {
    return (
      <div className="categories-page">
        <div className="categories-loading">
          <div className="loading-spinner"></div>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="categories-header">
        <div className="categories-header-left">
          <h1>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Categories
          </h1>
          <p className="categories-subtitle">
            Organize your products into categories for easier browsing
          </p>
        </div>
        <div className="categories-header-actions">
          {(!categories || categories.length === 0) && (
            <button
              className="categories-btn categories-btn-secondary"
              onClick={handleCreateDefaults}
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
              Import from Products
            </button>
          )}
          <button
            className="categories-btn categories-btn-primary"
            onClick={() => handleCreateCategory(null)}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Category
          </button>
        </div>
      </div>

      <div className="categories-filters">
        <div className="categories-search">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="categories-stats">
          <span>{categories?.length || 0} categories</span>
        </div>
      </div>

      {filteredCategories.length === 0 ? (
        <div className="categories-empty">
          <div className="categories-empty-icon">
            <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3>No categories yet</h3>
          <p>Create categories to organize your products</p>
          <div className="categories-empty-actions">
            <button
              className="categories-btn categories-btn-primary"
              onClick={() => handleCreateCategory(null)}
            >
              Create Category
            </button>
            <button
              className="categories-btn categories-btn-secondary"
              onClick={handleCreateDefaults}
            >
              Import from Products
            </button>
          </div>
        </div>
      ) : (
        <div className="categories-list">
          {filteredCategories.map(category => (
            <div key={category.id} className="category-item">
              <div className="category-row">
                <div className="category-expand">
                  {category.children && category.children.length > 0 ? (
                    <button
                      className="expand-btn"
                      onClick={() => toggleExpand(category.id)}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        style={{
                          transform: expandedCategories.has(category.id) ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.2s'
                        }}
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  ) : (
                    <span className="expand-spacer" />
                  )}
                </div>

                <div className="category-image">
                  {category.image ? (
                    <img src={category.image} alt={category.name} />
                  ) : (
                    <div className="category-image-placeholder">
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="category-info">
                  <h3 className="category-name">{category.name}</h3>
                  <div className="category-meta">
                    <span className="category-count">
                      {getProductCount(category)} products
                    </span>
                    {category.children && category.children.length > 0 && (
                      <span className="category-subcategories">
                        {category.children.length} subcategories
                      </span>
                    )}
                    {!category.isPublic && (
                      <span className="category-badge category-badge-private">Hidden</span>
                    )}
                  </div>
                </div>

                <div className="category-actions">
                  <button
                    className="category-action-btn"
                    onClick={() => handleCreateCategory(category)}
                    title="Add subcategory"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                  <button
                    className="category-action-btn"
                    onClick={() => handleEditCategory(category)}
                    title="Edit"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                  <button
                    className="category-action-btn category-action-delete"
                    onClick={() => handleDeleteCategory(category)}
                    title="Delete"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Subcategories */}
              {expandedCategories.has(category.id) && category.children && category.children.length > 0 && (
                <div className="subcategories-list">
                  {category.children.map(subcategory => (
                    <div key={subcategory.id} className="category-row subcategory-row">
                      <div className="category-expand">
                        <span className="subcategory-line" />
                      </div>

                      <div className="category-image">
                        {subcategory.image ? (
                          <img src={subcategory.image} alt={subcategory.name} />
                        ) : (
                          <div className="category-image-placeholder small">
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      <div className="category-info">
                        <h4 className="category-name">{subcategory.name}</h4>
                        <div className="category-meta">
                          <span className="category-count">
                            {subcategory._count?.products || 0} products
                          </span>
                          {!subcategory.isPublic && (
                            <span className="category-badge category-badge-private">Hidden</span>
                          )}
                        </div>
                      </div>

                      <div className="category-actions">
                        <button
                          className="category-action-btn"
                          onClick={() => handleEditCategory(subcategory)}
                          title="Edit"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                          </svg>
                        </button>
                        <button
                          className="category-action-btn category-action-delete"
                          onClick={() => handleDeleteCategory(subcategory)}
                          title="Delete"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Category Modal */}
      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCategory(null);
          setParentForNew(null);
        }}
        category={editingCategory}
        parentCategory={parentForNew}
        onSuccess={() => {
          dispatch(fetchCategories({ tree: true }));
        }}
      />
    </div>
  );
};

export default Categories;
